import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuinielaStore } from '../../store/quinielaStore'
import { useAuthStore } from '../../store/authStore'
import {
  ArrowLeft,
  Loader,
  RefreshCw,
  Calculator,
  Trophy,
  CheckCircle,
  AlertCircle,
  Crown,
  Medal,
  Award,
  Users
} from 'lucide-react'
import toast from 'react-hot-toast'

export default function QuinielaResults() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const {
    fetchQuinielaById,
    getQuinielaMatches,
    getParticipants,
    syncMatchResults,
    calculatePoints,
    finalizeQuiniela,
    loading
  } = useQuinielaStore()

  const [quiniela, setQuiniela] = useState(null)
  const [matches, setMatches] = useState([])
  const [participants, setParticipants] = useState([])
  const [syncing, setSyncing] = useState(false)
  const [calculating, setCalculating] = useState(false)
  const [finalizing, setFinalizing] = useState(false)

  useEffect(() => {
    if (id) {
      loadData()
    }
  }, [id])

  const loadData = async () => {
    try {
      // Cargar quiniela
      const quinielaResult = await fetchQuinielaById(id)
      if (quinielaResult.success) {
        setQuiniela(quinielaResult.data)
      }

      // Cargar partidos
      const matchesResult = await getQuinielaMatches(id)
      if (matchesResult.success) {
        setMatches(matchesResult.matches)
      }

      // Cargar participantes
      const participantsResult = await getParticipants(id)
      if (participantsResult.success) {
        setParticipants(participantsResult.participants)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Error al cargar los datos')
    }
  }

  const handleSyncResults = async () => {
    setSyncing(true)
    const result = await syncMatchResults(id)
    setSyncing(false)
    
    if (result.success) {
      await loadData()
    }
  }

  const handleCalculatePoints = async () => {
    setCalculating(true)
    const result = await calculatePoints(id)
    setCalculating(false)
    
    if (result.success) {
      await loadData()
    }
  }

  const handleFinalizeQuiniela = async () => {
    if (!window.confirm('¿Estás seguro de finalizar esta quiniela? Esta acción distribuirá los premios y no se puede deshacer.')) {
      return
    }

    setFinalizing(true)
    const result = await finalizeQuiniela(id)
    setFinalizing(false)
    
    if (result.success) {
      await loadData()
    }
  }

  const getMatchesWithResults = () => {
    return matches.filter(m => m.result?.winner).length
  }

  const canFinalize = () => {
    if (!quiniela) return false
    return quiniela.status !== 'finished' && 
           getMatchesWithResults() === matches.length &&
           matches.length > 0
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return ''
    const date = timestamp?.toDate?.() || new Date(timestamp)
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getPositionIcon = (position) => {
    switch(position) {
      case 1: return <Crown className="text-[#D4AF37]" size={24} />
      case 2: return <Medal className="text-[#BDBDBD]" size={24} />
      case 3: return <Award className="text-[#CD7F32]" size={24} />
      default: return null
    }
  }

  if (loading && !quiniela) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-[#1A1A1A] min-h-screen">
        <div className="flex justify-center items-center py-12">
          <Loader className="animate-spin text-[#22C55E]" size={40} />
        </div>
      </div>
    )
  }

  if (!quiniela) {
    return null
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-[#1A1A1A] min-h-screen">
      {/* Header */}
      <Link
        to="/operator"
        className="inline-flex items-center gap-2 text-[#BDBDBD] hover:text-[#F8F8F8] mb-6"
      >
        <ArrowLeft size={20} />
        Volver al panel
      </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#D4AF37] mb-2">
          Gestión de Resultados
        </h1>
        <p className="text-[#BDBDBD]">{quiniela.name}</p>
        <div className="mt-2">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            quiniela.status === 'finished' 
              ? 'bg-[#BDBDBD]/20 text-[#BDBDBD]' 
              : 'bg-[#0F3D2E]/20 text-[#22C55E]'
          }`}>
            {quiniela.status === 'finished' ? 'Finalizada' : 'En proceso'}
          </span>
        </div>
      </div>

      {/* Actions */}
      {quiniela.status !== 'finished' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <button
            onClick={handleSyncResults}
            disabled={syncing || loading}
            className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#0F3D2E]/20 rounded-lg">
                <RefreshCw className={`text-[#22C55E] ${syncing ? 'animate-spin' : ''}`} size={24} />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-[#F8F8F8]">Sincronizar Resultados</h3>
                <p className="text-sm text-[#BDBDBD]">Obtener resultados de la API</p>
              </div>
            </div>
          </button>

          <button
            onClick={handleCalculatePoints}
            disabled={calculating || loading || getMatchesWithResults() === 0}
            className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#0F3D2E]/20 rounded-lg">
                <Calculator className={`text-[#22C55E] ${calculating ? 'animate-pulse' : ''}`} size={24} />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-[#F8F8F8]">Calcular Puntos</h3>
                <p className="text-sm text-[#BDBDBD]">Actualizar puntuaciones</p>
              </div>
            </div>
          </button>

          <button
            onClick={handleFinalizeQuiniela}
            disabled={finalizing || loading || !canFinalize()}
            className="bg-[#222222] border border-[#D4AF37] rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#D4AF37]/20 rounded-lg">
                <Trophy className={`text-[#D4AF37] ${finalizing ? 'animate-bounce' : ''}`} size={24} />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-[#F8F8F8]">Finalizar Quiniela</h3>
                <p className="text-sm text-[#BDBDBD]">Distribuir premios</p>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-[#222222] p-6 rounded-lg border border-[#0F3D2E]">
          <p className="text-sm text-[#BDBDBD] mb-1">Total de Partidos</p>
          <p className="text-2xl font-bold text-[#F8F8F8]">{matches.length}</p>
        </div>
        <div className="bg-[#222222] p-6 rounded-lg border border-[#0F3D2E]">
          <p className="text-sm text-[#BDBDBD] mb-1">Con Resultados</p>
          <p className="text-2xl font-bold text-[#22C55E]">{getMatchesWithResults()}</p>
        </div>
        <div className="bg-[#222222] p-6 rounded-lg border border-[#0F3D2E]">
          <p className="text-sm text-[#BDBDBD] mb-1">Participantes</p>
          <p className="text-2xl font-bold text-[#F8F8F8]">{participants.length}</p>
        </div>
        <div className="bg-[#222222] p-6 rounded-lg border border-[#D4AF37]">
          <p className="text-sm text-[#BDBDBD] mb-1">Premio Total</p>
          <p className="text-2xl font-bold text-[#D4AF37]">${quiniela.prizePool.toFixed(0)}</p>
        </div>
      </div>

      {/* Info Alert */}
      {quiniela.status !== 'finished' && getMatchesWithResults() === 0 && matches.length > 0 && (
        <div className="mb-6 bg-[#0F3D2E]/10 border border-[#0F3D2E]/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-[#22C55E] flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h3 className="font-semibold text-[#F8F8F8] mb-1">
                No hay partidos finalizados aún
              </h3>
              <p className="text-sm text-[#BDBDBD]">
                Los resultados se sincronizarán automáticamente desde la API una vez que los partidos hayan finalizado. 
                Puedes intentar sincronizar ahora o esperar a que los partidos terminen.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Matches */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-[#F8F8F8]">Partidos</h2>
          {matches.length === 0 ? (
            <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-6 text-center py-12">
              <AlertCircle className="mx-auto mb-4 text-[#BDBDBD]" size={48} />
              <h3 className="text-lg font-semibold text-[#F8F8F8] mb-2">
                No hay partidos registrados
              </h3>
              <p className="text-[#BDBDBD]">
                Esta quiniela aún no tiene partidos asignados
              </p>
            </div>
          ) : (
            <div className="space-y-3">
            {matches.map((match) => (
              <div key={match.id} className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="font-semibold text-[#F8F8F8]">
                      {match.teams.home.name} vs {match.teams.away.name}
                    </div>
                    <div className="text-sm text-[#BDBDBD]">
                      {formatDate(match.date)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {match.result?.winner ? (
                      <>
                        <span className="text-xs px-2 py-1 bg-[#0F3D2E]/20 text-[#22C55E] rounded-full font-medium">
                          Finalizado
                        </span>
                        <CheckCircle className="text-[#22C55E]" size={20} />
                      </>
                    ) : (
                      <>
                        <span className="text-xs px-2 py-1 bg-[#D4AF37]/20 text-[#D4AF37] rounded-full font-medium">
                          Pendiente
                        </span>
                        <AlertCircle className="text-[#D4AF37]" size={20} />
                      </>
                    )}
                  </div>
                </div>
                {match.result && (
                  <div className="mt-2 p-2 bg-[#0F3D2E]/10 rounded">
                    <div className="text-sm font-semibold text-[#22C55E]">
                      Resultado: {match.result.home} - {match.result.away}
                      <span className="ml-2 text-xs">
                        (Ganador: {
                          match.result.winner === 'home' ? match.teams.home.name :
                          match.result.winner === 'away' ? match.teams.away.name :
                          'Empate'
                        })
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>
          )}
        </div>

        {/* Participants */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-[#F8F8F8]">Participantes</h2>
          {participants.length === 0 ? (
            <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-6 text-center py-12">
              <Users className="mx-auto mb-4 text-[#BDBDBD]" size={48} />
              <h3 className="text-lg font-semibold text-[#F8F8F8] mb-2">
                No hay participantes
              </h3>
              <p className="text-[#BDBDBD]">
                Aún no hay usuarios registrados en esta quiniela
              </p>
            </div>
          ) : (
            <div className="space-y-3">
            {participants.map((participant, index) => (
              <div key={participant.id} className={`bg-[#222222] border rounded-lg p-4 ${
                participant.position === 1 ? 'border-[#D4AF37] border-2' :
                participant.position === 2 ? 'border-[#BDBDBD] border-2' :
                participant.position === 3 ? 'border-[#CD7F32] border-2' :
                'border-[#0F3D2E]'
              }`}>
                <div className="flex items-center gap-4">
                  {participant.position && getPositionIcon(participant.position)}
                  <div className="flex-1">
                    <div className="font-semibold text-[#F8F8F8]">
                      {participant.username}
                    </div>
                    <div className="text-sm text-[#BDBDBD]">
                      {participant.points || 0} puntos • {participant.correctPredictions || 0} aciertos
                    </div>
                  </div>
                  {participant.prize > 0 && (
                    <div className="text-right">
                      <div className="text-lg font-bold text-[#D4AF37]">
                        ${participant.prize}
                      </div>
                      <div className="text-xs text-[#BDBDBD]">
                        {participant.position}° lugar
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
